import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { CalendarIcon, Copy, EllipsisVertical } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router'
import z from 'zod'

import { DataTable } from '@/components/DataTable'
import { Button } from '@/components/elements/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/elements/dropdown-menu'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Separator } from '@/components/elements/separator'
import { Skeleton } from '@/components/elements/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { MultiSelect } from '@/components/MultiSelect'
import { RosterUpload } from '@/components/RosterUpload'
import { Select } from '@/components/Select'
import { SlidePanel } from '@/components/SlidePanel'
import { StatusBadge } from '@/components/StatusBadge'
import { getEntityCertificateStudents } from '@/services/entity_student_certificates.service'
import { unregisterForEvent, getEvent, registerForEvent } from '@/services/events.service'
import { formatDate, formatTimeRange } from '@/utils/formatters'
import { toast, uniqueEmails } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import BulkGrantCertificatesModal from './BulkGrantCertificatesModal'

const EventSlideoutInstructorView = ({ viewEvent, resetAndClose, canManage = true }) => {
  // Data Loading

  // Load event details to ensure we have the expanded data
  const { data: event, isPending } = useQuery({
    queryKey: ['events', viewEvent.id],
    queryFn: () => getEvent(viewEvent.id),
  })
  // Make sure we tear down on navigation
  useEffect(
    () => () => {
      document.body.style.pointerEvents = ''
    },
    [resetAndClose],
  )

  if (isPending) {
    return (
      <SlidePanel
        title={viewEvent.name || 'Event'}
        loading
        setOpen={resetAndClose}
        contentClassName="sm:w-[50vw]! sm:min-w-[50vw]! sm:max-w-[50vw]!"
        content={
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        }
      />
    )
  }
  return (
    <SlidePanel
      setOpen={resetAndClose}
      title={event.name}
      loading={isPending}
      contentClassName="sm:w-[50vw]! sm:min-w-[50vw]! sm:max-w-[50vw]!"
      actions={[
        {
          label: 'Close',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
      ]}
      content={
        <Tabs defaultValue="details">
          <TabsList className="mb-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            {canManage && <TabsTrigger value="registrations">Registrations</TabsTrigger>}
          </TabsList>
          <TabsContent value="details">
            {' '}
            <div className="flex flex-col gap-6">
              <div className="text-muted-foreground flex gap-4 text-sm">
                <CalendarIcon className="h-6 w-6" />
                <div className="flex flex-col gap-2">
                  <p>
                    <strong>Date: </strong> {formatDate(event.date)}
                  </p>
                  <p>
                    <strong>Time: </strong> {formatTimeRange(event.startTime, event.endTime)}
                  </p>
                  <p>
                    <strong>Capacity: </strong> {event.registrations ?? 0}/{event.capacity}{' '}
                    registrations
                  </p>
                </div>
              </div>

              {canManage && <RosterUpload event={event} />}

              {/* eslint-disable-next-line react/no-danger */}
              <p dangerouslySetInnerHTML={{ __html: event.description }} />
              <Separator />
              <h3>Included Trainings</h3>
              <p>
                This event covers all trainings needed to complete the hands-on requirement for the
                following certificates:
              </p>
              <ul className="list-disc pl-5">
                {event.certificates.map((cert) => (
                  <li key={cert.id}>{cert.name}</li>
                ))}
              </ul>
              {event.additionalTrainings.length > 0 && (
                <>
                  <p>This event also includes the following additional trainings:</p>
                  <ul className="list-disc pl-5">
                    {event.additionalTrainings.map((training) => (
                      <li key={training.id}>{training.name}</li>
                    ))}
                  </ul>
                </>
              )}
              <Separator />
              <p>
                <strong>Note:</strong> Students must complete all online course material before
                taking the hands-on training.
              </p>
            </div>
          </TabsContent>
          {canManage && (
            <TabsContent value="registrations">
              <RegistrationsTab event={event} />
            </TabsContent>
          )}
        </Tabs>
      }
    />
  )
}

EventSlideoutInstructorView.propTypes = {
  viewEvent: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
  canManage: PropTypes.bool,
}

export default EventSlideoutInstructorView

function RegistrationsTab({ event }) {
  const [showBulkGrantModal, setShowBulkGrantModal] = useState(false)

  const hasRoster = !!event.roster
  const studentCertificates = useMemo(
    () => event.studentCertificates || [],
    [event.studentCertificates],
  )

  // Calculate eligible students count
  const eligibleCount = useMemo(
    () =>
      studentCertificates.filter((sc) => {
        // Already has certificate
        if (sc.status === 'Issued') return false

        // Student is eligible if all their course records are completed
        // We don't require training to be complete since the event IS the training
        const courseRecords = sc.courseRecords || []
        if (courseRecords.length === 0) return false

        const allCoursesCompleted = courseRecords.every((cr) => cr.status === 'Completed')
        return allCoursesCompleted
      }).length,
    [studentCertificates],
  )

  return (
    <div className="flex flex-col gap-6">
      <p>
        <strong>Capacity: </strong> {event.registrations ?? 0}/{event.capacity} registrations
      </p>
      {!hasRoster && (
        <div className="bg-warning/10 border-warning rounded-md border p-4">
          <p className="text-warning font-semibold">Roster Required</p>
          <p className="mt-2 text-sm">
            A roster must be uploaded to the event before certificates can be granted.
          </p>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => setShowBulkGrantModal(true)}
          disabled={!hasRoster || eligibleCount === 0}
        >
          Bulk Grant Certificates ({eligibleCount} eligible)
        </Button>
        <Button
          variant="outline"
          disabled={studentCertificates.length === 0}
          onClick={() => {
            const emails = uniqueEmails(studentCertificates.map((sc) => sc.student?.user?.email))
            navigator.clipboard.writeText(emails.join(', ')).then(() => {
              toast('Emails copied to clipboard')
            })
          }}
        >
          <Copy className="h-4 w-4" />
          Copy Emails
        </Button>
      </div>
      <RegistrationsTable event={event} />
      <AddRegistration event={event} />
      {showBulkGrantModal && (
        <BulkGrantCertificatesModal event={event} onClose={() => setShowBulkGrantModal(false)} />
      )}
    </div>
  )
}
RegistrationsTab.propTypes = {
  event: PropTypes.object.isRequired,
}

function RegistrationsTable({ event }) {
  const data = event.studentCertificates || []

  const { entityId } = useParams()

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const { mutate: mutateCancelation } = useMutation({
    mutationFn: (studentCertificate) => unregisterForEvent(event.id, [studentCertificate]),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('You have successfully cancelled the registration.')
      // Refresh events on calendar
      queryClient.invalidateQueries({ queryKey: ['events'] })
      // Refresh instructor view of certificates
      queryClient.invalidateQueries({ queryKey: ['accounts', 'instructors', 'certificates'] })
    },
  })

  const renderInfoColumn = (info) => {
    const studentId = info.row.original.student.id
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" aria-label="Edit registrations menu">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/accounts/${entityId}/instructors/students/${studentId}`}>
              View Student
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => mutateCancelation(info.row.original.id)}
            variant="destructive"
          >
            Cancel Event Registration
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const renderCertificateStatusColumn = (info) => (
    <StatusBadge status={info.getValue()} size="sm" type="studentCertificate" />
  )

  const getOnlineContentStatus = (studentCertificate) => {
    const courseRecords = studentCertificate.courseRecords || []
    if (courseRecords.length === 0) return 'Not Started'

    const completedCount = courseRecords.filter((cr) => cr.status === 'Completed').length
    const totalCount = courseRecords.length

    if (completedCount === totalCount) return 'Completed'
    if (completedCount > 0) return `In Progress (${completedCount}/${totalCount})`
    return 'Not Started'
  }

  const columnHelper = createColumnHelper()
  const columns = [
    columnHelper.accessor((row) => formatUserName(row?.student?.user), {
      id: 'name',
      header: 'Name',
    }),
    columnHelper.accessor((row) => getOnlineContentStatus(row), {
      id: 'onlineContent',
      header: 'Online Content',
    }),
    columnHelper.accessor('status', {
      id: 'certificateStatus',
      header: 'Certificate Status',
      cell: (info) => renderCertificateStatusColumn(info),
    }),
    columnHelper.accessor('id', {
      id: 'info',
      size: 15,
      header: 'Edit',
      cell: (info) => renderInfoColumn(info),
    }),
  ]

  return <DataTable columns={columns} data={data} rowCount={data.length || 0} hidePagination />
}
RegistrationsTable.propTypes = {
  event: PropTypes.object.isRequired,
}

function AddRegistration({ event }) {
  // Context
  const { entityId } = useParams()

  // Form
  const schema = z.object({
    certificate: z.string().min(1, 'Please select a certificate.'),
    studentCertificates: z
      .array(z.string())
      .min(1, 'Please select at least one student.')
      .nonempty('Please select at least one student.'),
  })
  const form = useForm({
    defaultValues: {
      certificate: event.certificates[0]?.id || '',
      studentCertificates: [],
    },
    resolver: zodResolver(schema),
  })
  const selectedCertificate = form.watch('certificate')
  useEffect(() => {
    form.resetField('studentCertificates')
  }, [selectedCertificate, form])

  // Data Loading and mutations

  const { data, isPending } = useQuery({
    queryKey: [
      'accounts',
      'instructors',
      'certificates',
      selectedCertificate,
      'students',
      entityId,
    ],
    queryFn: () =>
      getEntityCertificateStudents(entityId, selectedCertificate, {
        pagination: { pageIndex: 0, pageSize: 1000 },
        filters: {},
      }),
  })

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending: registrationPending, mutate: mutateEvent } = useMutation({
    mutationFn: (/** @type string */ studentCertificates) =>
      registerForEvent(event.id, studentCertificates),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('You have successfully registered for this event.')
      // Refresh events on calendar
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })

  // Data processing
  const CERTIFICATE_OPTIONS = event.certificates.map((certificate) => ({
    label: certificate.name,
    id: certificate.id,
  }))

  const STUDENT_OPTIONS = useMemo(() => {
    const registeredStudentIds = event.studentCertificates?.map((sc) => sc.student.id) || []
    return (
      data?.results
        .filter(({ student }) => !registeredStudentIds.includes(student.id))
        .map((student) => ({
          label: formatUserName(student.student.user),
          id: student.id,
        })) ?? []
    )
  }, [data?.results, event.studentCertificates])

  const registerStudentCertificates = ({ studentCertificates }) => {
    mutateEvent(studentCertificates)
  }

  if (event.capacity !== undefined && event.registrations >= event.capacity) {
    return (
      <section>
        <h3>Event Full</h3>
        <p className="text-sm text-gray-500">
          This event has reached its capacity and cannot accept additional registrations.
        </p>
      </section>
    )
  }
  return (
    <section>
      <h3>Register Additional Students</h3>
      <Form {...form}>
        <form
          className="flex flex-col gap-4 py-1"
          onSubmit={form.handleSubmit(registerStudentCertificates)}
        >
          <FormField
            control={form.control}
            name="certificate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Course</FormLabel>
                <FormControl>
                  <Select options={CERTIFICATE_OPTIONS} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="studentCertificates"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Student(s)</FormLabel>
                <FormDescription>
                  Shows all students assigned to a course covered by this training who are not yet
                  registered.
                </FormDescription>
                <FormControl>
                  <MultiSelect
                    options={STUDENT_OPTIONS}
                    {...field}
                    width="w-full"
                    placeholder="Choose students"
                    disabled={isPending || STUDENT_OPTIONS.length === 0}
                    rules={{
                      validate: (v) => v.length > 0 || 'Please select at least one student.',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            disabled={isPending || !form.formState.isValid || registrationPending}
          >
            Register Students
          </Button>
        </form>
      </Form>
    </section>
  )
}

AddRegistration.propTypes = {
  event: PropTypes.object.isRequired,
}
